import { useDialog } from '@dni-hooks/useDialog'
import { Dialog, DialogActions, DialogContent, MenuItem, Select } from '@fortune-ui'
import { mutateAllECommerceOrders } from '@hooks-api/ec/useApiECommerceOrders'
import { useApiECommerceUpdateOrder } from '@hooks-api/ec/useApiECommerceUpdateOrder'
import { type OrderItem, type ServiceOrderItem } from '@hooks-api/types/dataModel/eCommerceOrders'
import { useApiProductSubshipVariationSetting } from '@hooks-api/wp/useApiProductSubshipVariationSetting'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { CircularProgress } from '@pets-ui/CircularProgress/CircularProgress'
import { HStack } from '@pets-ui/HStack/HStack'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { REVISE_DELIVERY_AMOUNT_DIALOG_KEY, SERVER_ERROR_DIALOG_KEY } from '../constants/dialogKey'

export interface DialogReviseDeliveryAmountProps {
  serviceOrderItems: ServiceOrderItem[]
  remainDeliveryAt: string[]
  fortuneOrderId: string
  productId: OrderItem['product_id']
  variationId: OrderItem['variation_id']
}

export interface DialogReviseDeliveryAmountComponentProps extends DialogReviseDeliveryAmountProps {
  onClose: () => void
}

const DialogReviseDeliveryAmountComponent = ({ onClose, productId, variationId, remainDeliveryAt, serviceOrderItems, fortuneOrderId }: DialogReviseDeliveryAmountComponentProps) => {
  const { openDialog: openServerErrorDialog } = useDialog(SERVER_ERROR_DIALOG_KEY)
  const { t } = useTranslation()
  const quantityPerDelivery = serviceOrderItems?.[0]?.quantity_per_delivery
  const [value, setValue] = useState<string>(String(quantityPerDelivery))
  const remainQuantity = serviceOrderItems?.[0]?.remain_quantity
  const { destructuredData: productSettings, isLoading } = useApiProductSubshipVariationSetting(productId || variationId ? { parent_product_id: productId, product_id: variationId } : undefined)
  const options = productSettings?.subship_setting.settings[0]?.amounts.filter(amount => remainQuantity && amount.value <= remainQuantity && amount.enabled).map(amount => ({ key: String(amount.value), value: String(amount.value) }))
  const unit = productSettings?.subship_setting.settings[0]?.amount_unit
  const { trigger, isMutating } = useApiECommerceUpdateOrder()

  const handleClickSubmit = async () => {
    try {
      await trigger({
        id: fortuneOrderId,
        service_order_items: [{
          // @ts-expect-error - TODO: fix this
          product_id: serviceOrderItems?.[0]?.product_id,
          quantity_per_delivery: Number(value),
        }],
        remain_delivery_at: remainDeliveryAt,
      })
      onClose()
    }
    catch (e) {
      console.error('Update delivery amount error', e)
      onClose()
      openServerErrorDialog()
    }
    finally {
      mutateAllECommerceOrders?.()
    }
  }

  return (
    <Box p={4}>
      <Text align="center" variant="h4">
        {t('wpOrder.reviseDeliveryAmount')}
      </Text>
      <DialogContent sx={{ p: 0, m: '8px 0 16px 0', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40px', width: '100%' }}>
        {isLoading
          ? <CircularProgress fontSize={20} color={colors.grey300} />
          : options
            && (
              <Select
                key={value}
                fullWidth
                value={value}
                options={options}
                variant="outlined"
                onChange={(e) => {
                  const newValue = e.target.value as string
                  setValue(newValue)
                }}
                renderOption={options.map(({ key, value }) => <MenuItem key={key} value={value}>{t('wpOrder.deliveryPer', { value, unit: t(`wpOrderUnit.${unit}`) })}</MenuItem>)}
              />
            )}
      </DialogContent>
      <DialogActions sx={{ p: 0 }}>
        <HStack gap={2} flexWrap="nowrap" width="100%">
          <Button loading={isMutating} fullWidth variant="outlined" onClick={onClose}>{t('wpOrder.cancel')}</Button>
          <Button loading={isMutating} fullWidth variant="filled" onClick={handleClickSubmit}>{t('wpOrder.submit')}</Button>
        </HStack>
      </DialogActions>
    </Box>
  )
}

export function DialogReviseDeliveryAmount() {
  const { open, closeDialog, productId, variationId, remainDeliveryAt, serviceOrderItems, fortuneOrderId } = useDialog<DialogReviseDeliveryAmountProps>(REVISE_DELIVERY_AMOUNT_DIALOG_KEY)

  return (
    <Dialog
      maxWidth="xs"
      open={open}
      onClose={closeDialog}
      disablePortal
      sx={{
        '& .MuiPaper-root': {
          maxWidth: '320px',
          borderRadius: '8px',
        },
      }}
    >
      <DialogReviseDeliveryAmountComponent onClose={closeDialog} productId={productId} variationId={variationId} fortuneOrderId={fortuneOrderId} remainDeliveryAt={remainDeliveryAt} serviceOrderItems={serviceOrderItems} />
    </Dialog>
  )
}
